import { Table, Form, Button } from 'react-bootstrap';
import { useGetIncomesQuery } from '../../slices/incomesApiSlice';
import { useGetExpensesQuery } from '../../slices/expensesApiSlice';
import { useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const incomeCategories = ["Morning", "AfterNoon", "Night", "Other"];
const expenseCategories = [
  "SalesCredit", "SalesPayment", "Consult", "Lab", "Dressing", "Delivery",
  "Observation", "Admission", "Other", "Electricity", "Expense", "Service", "Office Supplies"
];

const IncomeExpenseCategories = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { data: incomesData } = useGetIncomesQuery({ startDate, endDate });
  const { data: expensesData } = useGetExpensesQuery({ startDate, endDate });

  // Group by date
  const groupByDate = (data, categories) => {
    const grouped = {};
    data?.forEach(entry => {
      const date = new Date(entry.date).toISOString().split('T')[0];
      if (date >= startDate && date <= endDate) {
        if (!grouped[date]) grouped[date] = {};
        const cat = entry.category;
        const amt = Number(entry.amount || 0);
        grouped[date][cat] = (grouped[date][cat] || 0) + amt;
      }
    });
    return grouped;
  };

  const groupedIncomeByDate = groupByDate(incomesData?.incomes, incomeCategories);
  const groupedExpenseByDate = groupByDate(expensesData?.expenses, expenseCategories);

  const getColumnTotals = (grouped, categories) => {
    const totals = {};
    Object.values(grouped).forEach(entry => {
      categories.forEach(cat => {
        totals[cat] = (totals[cat] || 0) + (entry[cat] || 0);
      });
    });
    return totals;
  };

  const incomeColumnTotals = getColumnTotals(groupedIncomeByDate, incomeCategories);
  const expenseColumnTotals = getColumnTotals(groupedExpenseByDate, expenseCategories);

  const totalIncome = Object.values(incomeColumnTotals).reduce((sum, val) => sum + val, 0);
  const totalExpenses = Object.values(expenseColumnTotals).reduce((sum, val) => sum + val, 0);

  // Export to Excel
  const handleExport = () => {
    const buildSheet = (grouped, categories, label) => {
      const rows = Object.entries(grouped).map(([date, entry]) => {
        const row = { Date: date };
        let rowTotal = 0;
        categories.forEach(cat => {
          const val = entry[cat] || 0;
          row[cat] = val;
          rowTotal += val;
        });
        row[label] = rowTotal;
        return row;
      });

      const summary = { Date: 'Total' };
      categories.forEach(cat => {
        summary[cat] = Object.values(grouped).reduce((sum, entry) => sum + (entry[cat] || 0), 0);
      });
      summary[label] = rows.reduce((sum, r) => sum + r[label], 0);
      rows.push(summary);

      return XLSX.utils.json_to_sheet(rows);
    };

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, buildSheet(groupedIncomeByDate, incomeCategories, 'Total Income'), 'Income Report');
    XLSX.utils.book_append_sheet(workbook, buildSheet(groupedExpenseByDate, expenseCategories, 'Total Expenses'), 'Expense Report');

    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `Income_Expense_Report_${startDate}_to_${endDate}.xlsx`);
  };

  return (
    <div className="container mt-4">
      <h1 className="text-center">Income & Expense Report</h1>

      <Form className="mb-3 d-flex justify-content-center">
        <Form.Group className="mx-2">
          <Form.Label>Start Date:</Form.Label>
          <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </Form.Group>
        <Form.Group className="mx-2">
          <Form.Label>End Date:</Form.Label>
          <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </Form.Group>
      </Form>

      {startDate && endDate && (
        <>
          {/* Income Table */}
          <h4 className="mt-4">Income Report</h4>
          <Table striped bordered hover responsive>
            <thead className="text-center">
              <tr>
                <th>Date</th>
                {incomeCategories.map(cat => <th key={cat}>{cat}</th>)}
                <th>Total Income</th>
              </tr>
            </thead>
            <tbody className="text-center">
              {Object.entries(groupedIncomeByDate).map(([date, entry]) => {
                const rowTotal = incomeCategories.reduce((sum, cat) => sum + (entry[cat] || 0), 0);
                return (
                  <tr key={date}>
                    <td>{date}</td>
                    {incomeCategories.map(cat => <td key={cat}>{(entry[cat] || 0).toFixed(2)}</td>)}
                    <td>{rowTotal.toFixed(2)}</td>
                  </tr>
                );
              })}
              <tr className="fw-bold">
                <td>Total</td>
                {incomeCategories.map(cat => <td key={cat}>{(incomeColumnTotals[cat] || 0).toFixed(2)}</td>)}
                <td>{totalIncome.toFixed(2)}</td>
              </tr>
            </tbody>
          </Table>

          {/* Expense Table */}
          <h4 className="mt-5">Expense Report</h4>
          <Table striped bordered hover responsive>
            <thead className="text-center">
              <tr>
                <th>Date</th>
                {expenseCategories.map(cat => <th key={cat}>{cat}</th>)}
                <th>Total Expenses</th>
              </tr>
            </thead>
            <tbody className="text-center">
              {Object.entries(groupedExpenseByDate).map(([date, entry]) => {
                const rowTotal = expenseCategories.reduce((sum, cat) => sum + (entry[cat] || 0), 0);
                return (
                  <tr key={date}>
                    <td>{date}</td>
                    {expenseCategories.map(cat => <td key={cat}>{(entry[cat] || 0).toFixed(2)}</td>)}
                    <td>{rowTotal.toFixed(2)}</td>
                  </tr>
                );
              })}
              <tr className="fw-bold">
                <td>Total</td>
                {expenseCategories.map(cat => <td key={cat}>{(expenseColumnTotals[cat] || 0).toFixed(2)}</td>)}
                <td>{totalExpenses.toFixed(2)}</td>
              </tr>
            </tbody>
          </Table>

          <div className="text-center mt-4">
            <Button variant="primary" onClick={handleExport}>
              Export Combined Report to Excel
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default IncomeExpenseCategories;